import React, { FC, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import DialogLayout from "../DialogLayout";
import Typography from "../../components/Typography/Typography";
import Button from "../../components/Button/Button";
import ASSETS from "../../assets";

export type AttestationVerificationStatus = "verified" | "unverified";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  defaultVerified: boolean;
  onConfirm: (status: boolean) => void;
}

const AttestationStatusModal: FC<IProps> = ({
  open,
  setOpen,
  defaultVerified,
  onConfirm,
}) => {
  const canSelectVerified = defaultVerified;
  const [selectedStatus, setSelectedStatus] =
    useState<AttestationVerificationStatus>(
      canSelectVerified ? "verified" : "unverified",
    );

  useEffect(() => {
    if (open) {
      setSelectedStatus(canSelectVerified ? "verified" : "unverified");
    }
  }, [open, canSelectVerified]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = () => {
    onConfirm(selectedStatus === "verified");
    setOpen(false);
  };

  const options = [
    {
      value: "verified" as const,
      label: "Verified Attestation",
      description: "Identity-backed attestation with face verification.",
      disabled: !canSelectVerified,
    },
    {
      value: "unverified" as const,
      label: "Unverified Attestation",
      description: "Create an attestation without verifying your identity.",
      disabled: false,
    },
  ];

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[700px] px-[50px] py-[45px] bg-white rounded-[15px] flex flex-col relative">
        <div
          className="absolute top-[25px] right-[25px] rounded-full size-[30px] bg-[#0000000D] cursor-pointer grid place-items-center"
          onClick={handleClose}
        >
          <img src={ASSETS.cross} alt="" />
        </div>

        <Typography variant="h2" className="font-bold title pr-8">
          Choose Attestation Method
        </Typography>
        <Typography variant="body1" color="grey" className="mt-[5px] mb-8">
          Select how you would like to generate your attestation.
        </Typography>

        <div className="flex gap-4 mb-10">
          {options.map((option) => {
            const isSelected = selectedStatus === option.value;

            return (
              <div key={option.value} className="relative group flex-1">
                <button
                  type="button"
                  disabled={option.disabled}
                  onClick={() => {
                    if (!option.disabled) {
                      setSelectedStatus(option.value);
                    }
                  }}
                  className={twMerge(
                    "w-full h-full text-left flex gap-3 p-4 rounded-[12px] border transition-colors",
                    isSelected
                      ? "border-k-light-blue bg-white"
                      : "border-k-border bg-white hover:bg-[#00000005]",
                    option.disabled &&
                      "opacity-50 cursor-not-allowed hover:bg-white",
                  )}
                >
                  <span
                    className={twMerge(
                      "mt-0.5 size-[18px] min-w-[18px] rounded-[4px] border grid place-items-center",
                      isSelected
                        ? "bg-k-light-blue border-k-light-blue"
                        : "border-[#C7C7CC] bg-white",
                    )}
                  >
                    {isSelected && (
                      <img src={ASSETS.tick_small} alt="" className="w-2.5" />
                    )}
                  </span>
                  <span className="flex flex-col gap-1">
                    <Typography
                      variant="body1"
                      className="font-[660] text-k-black"
                    >
                      {option.label}
                    </Typography>
                    <Typography variant="body2" color="grey">
                      {option.description}
                    </Typography>
                  </span>
                </button>

                {option.disabled && (
                  <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-max max-w-[240px] -translate-x-1/2 rounded-lg bg-k-black px-3 py-2 text-center opacity-0 transition-opacity group-hover:opacity-100">
                    <Typography variant="body2" color="white">
                      Please Verify yourself First from Profile
                    </Typography>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex gap-3">
          <Button
            label="Cancel"
            variant="outlined"
            textColor="blue"
            className="flex-1"
            onClick={handleClose}
          />
          <Button
            label="Continue"
            variant="primary"
            className="flex-1"
            onClick={handleSave}
          />
        </div>
      </div>
    </DialogLayout>
  );
};

export default AttestationStatusModal;
